import type { BookingPage } from '../../api/transport'
import { dayLabel, dayOf, timeOf } from './tripTimes'

/** Words the booking's pages share (M40-T09, T10). */

/** Medcor's registered name and address on the receipt: a placeholder until
 * the owner supplies them (spec §K Q15). */
export const MEDCOR_REGISTERED = {
  name: 'Medcor [registered company name to be confirmed]',
  address: '[Registered address to be confirmed], Accra, Ghana',
}

const METHOD: Record<string, string> = {
  mobile_money: 'Mobile Money',
  card: 'Card',
  bank_transfer: 'Bank transfer',
}

/** How a booking was paid, in words. An unlisted method reads as words, not
 *  a code (the payment side names them in M41). Shared with the desk. */
export function paymentMethodWords(method: string): string {
  return METHOD[method] ?? method.replace(/_/g, ' ')
}

/** "Wed 21 Oct, 12:00", Accra time. */
export function whenLabel(iso: string): string {
  const moment = new Date(iso)
  return `${dayLabel(dayOf(moment))}, ${timeOf(moment)}`
}

/** The passenger's first name, or the booker's when none is known. */
export function firstName(page: BookingPage): string {
  return page.passenger_first_name ?? page.booker_first_name
}

/**
 * Whether "Ready to collect" is offered (M40-T10): T08's rule mirrored, so
 * the button shows only when the server would accept it. A call-to-collect
 * return, from the outbound pickup time on, with a driver assigned and the
 * return not yet under way (T08 refuses `confirmed`: no driver ever came).
 */
export function canAskForReturn(page: BookingPage, now: Date): boolean {
  return (
    page.return_mode === 'call_to_collect' &&
    ['driver_assigned', 'picked_up', 'arrived'].includes(page.status) &&
    now.getTime() >= Date.parse(page.pickup_at)
  )
}

/** The words for a booking that will not, or did not, travel as planned. */
export function statusWords(status: BookingPage['status']): string | null {
  switch (status) {
    case 'cannot_fulfil':
      return "We're sorry — we couldn't provide this trip. You'll get a full refund."
    case 'cancelled_by_customer':
      return 'Cancelled'
    case 'completed':
      return 'Trip complete'
    case 'no_show':
      return 'The driver could not find you, so this trip did not go ahead.'
    default:
      return null
  }
}
